import React, { useContext, useState } from "react";
import {
  StyleSheet,
  Text,
  Pressable,
  View,
  TouchableOpacity,
  TextInput,
  ScrollView,
} from "react-native";
import { SessionContext } from "../../../../contexts";
import { Check, Close, Copy } from "../../../../assets/index";
import { SecondaryButton, NormalText } from "../../../common/native/index";
import { clientDomain } from "../../../../config";
import {
  TPatchPatientsInviteQuery,
  patchPatientsInvite,
  postClinicUserInvite,
} from "../../../../services/api";
import {
  TGetPatientsResponse,
  TPatientInfo,
  getPatients,
} from "../../../../services/api-clinic";
import { TClinicPatientType } from "../../../../utils/types";
import { buildStaffInviteRedirectUrl } from "../../../../utils/inviteRedirect";

type InviteAMemberModalProps = {
  onClose: () => void;
};

const InviteAMemberModal: React.FC<InviteAMemberModalProps> = ({ onClose }) => {
  const session = useContext(SessionContext)?.session;
  const isClinicAdmin = session?.role === "CLINIC_ADMIN";
  const [inviteType, setInviteType] = useState<"Patient" | "Staff" | null>(
    null,
  );

  const [selectedPatient, setSelectedPatient] = useState<TPatientInfo | null>(
    null,
  );
  const [staffEmail, setStaffEmail] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [patients, setPatients] = useState<TGetPatientsResponse>([]);
  const [filteredPatients, setFilteredPatients] =
    useState<TGetPatientsResponse>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorPatientInviteCode, setErrorPatientInviteCode] =
    useState<string>("");
  const [errorInviteStaffEmail, setErrorInviteStaffEmail] =
    useState<string>("");

  const [patientInviteCode, setPatientInviteCode] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);

  React.useEffect(() => {
    const fetchPatients = async () => {
      try {
        if (!session) return;
        const response: TGetPatientsResponse = await getPatients(session, [
          TClinicPatientType.PATIENT,
        ]);
        setPatients(response);
        setFilteredPatients(response);
      } catch (error) {
      } finally {
      }
    };
    fetchPatients();
  }, [session]);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      const lowerCaseTerms = searchTerm.toLowerCase().split(/\s+/); // Split by spaces

      const filtered = patients.filter((patient) => {
        const combinedInfo =
          `${patient.firstName} ${patient.lastName} ${patient.email} ${patient.phone}`.toLowerCase();

        // Ensure all search terms match somewhere in the combined fields
        return lowerCaseTerms.every((term) => combinedInfo.includes(term));
      });

      setFilteredPatients(filtered);
    }, 500); // 500ms debounce time

    return () => clearTimeout(handler);
  }, [searchTerm, patients]);

  const copyToClipboard = (inviteCode: string) => {
    navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const InviteOption = React.memo(
    ({
      title,
      description,
      children,
    }: {
      title: string;
      description?: string;
      children: React.ReactNode;
    }) => (
      <View style={[styles.optionContainer, styles.shadowBox]}>
        <NormalText text={title} fontWeight="600" />
        {description && (
          <NormalText text={description} style={styles.optionDescription} />
        )}
        <React.Fragment>{children}</React.Fragment>
      </View>
    ),
  );

  const handleGetPatientInviteCode = async (patientUUID: string) => {
    try {
      if (!session) {
        throw new Error("Session is missing");
      }
      const query: TPatchPatientsInviteQuery = {
        patient_uuid: patientUUID,
      };
      const response = await patchPatientsInvite(session, query);
      setPatientInviteCode(response.invite_code);
    } catch (error) {
      if (error instanceof Error) {
        setErrorPatientInviteCode(error.message);
      } else {
        setErrorPatientInviteCode(
          "An unexpected error occurred while getting patient invite code",
        );
      }
    } finally {
    }
  };

  const handleSendEmailInviteStaff = async () => {
    if (!isClinicAdmin) {
      setErrorInviteStaffEmail("Only clinic admins can send staff invites.");
      return;
    }

    try {
      setLoading(true);
      setErrorInviteStaffEmail("");

      if (!session || !session.clinicId) {
        throw new Error("Session is missing");
      }
      const normalizedEmail = staffEmail.trim();
      if (!normalizedEmail) {
        throw new Error("Please enter an email address.");
      }
      await postClinicUserInvite(session, {
        email: normalizedEmail,
        role: "CLINIC_STAFF",
        redirect_to: buildStaffInviteRedirectUrl(
          clientDomain,
          session.clinicId,
        ),
      });
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        setErrorInviteStaffEmail(error.message);
      } else {
        setErrorInviteStaffEmail(
          "An unexpected error occurred while inviting member",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPatientSearch = () => (
    <View style={{ position: "relative" }}>
      <TextInput
        placeholder="Search for a patient"
        value={searchTerm}
        onChangeText={setSearchTerm}
        style={styles.input}
      />
      <ScrollView
        style={[styles.scrollView, styles.shadowBox]}
        showsVerticalScrollIndicator={false}
      >
        {filteredPatients.map((patient, index) => (
          <View key={index} style={styles.patientListItem}>
            <input
              type="checkbox"
              checked={false}
              onClick={async () => {
                setSelectedPatient(patient);
                await handleGetPatientInviteCode(patient.uuid);
              }}
              style={{
                accentColor: "#1677FF",
              }}
            />
            <View style={styles.patientInfo}>
              <NormalText text={`${patient.firstName} ${patient.lastName}`} />
              <NormalText text={patient.email} style={styles.smallText} />
              <NormalText text={patient.phone} style={styles.smallText} />
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const renderStaffOptions = () => (
    <View style={styles.staffContainer}>
      <NormalText
        text="Invite a Clinic Staff Member"
        style={styles.staffTitle}
      />
      {!isClinicAdmin && (
        <NormalText
          text="Inviting staff requires clinic admin privileges."
          style={styles.errorText}
        />
      )}
      <NormalText text="This will grant permission to view all patient images" />

      <InviteOption
        title="Option 1: Share an Invite Code"
        description="The staff member can use this code to join your clinic after signing up."
      >
        <View style={styles.codeContainer}>
          <NormalText
            text={isClinicAdmin ? (session?.inviteCode ?? "") : "Admins only"}
          />
          <TouchableOpacity
            disabled={!isClinicAdmin}
            onPress={() => copyToClipboard(session?.inviteCode ?? "")}
            style={!isClinicAdmin ? styles.disabledInviteAction : null}
          >
            {copied ? <Check /> : <Copy />}
          </TouchableOpacity>
        </View>
        {!isClinicAdmin && (
          <NormalText
            text="Only clinic admins can copy and share staff invite codes."
            style={styles.errorText}
          />
        )}
      </InviteOption>

      <View style={[styles.optionContainer, styles.shadowBox]}>
        <NormalText text={"Option 2: Send and Email Invite"} fontWeight="600" />
        <TextInput
          placeholder="Enter email address"
          style={styles.input}
          value={staffEmail}
          onChangeText={setStaffEmail}
        />
        {errorInviteStaffEmail ? (
          <View style={{ marginHorizontal: "auto" }}>
            <NormalText
              text={errorInviteStaffEmail}
              style={{ color: "red", textAlign: "center" }}
            />
          </View>
        ) : null}
        <View style={styles.centeredButton}>
          <SecondaryButton
            text="Send Email Invite"
            roundBorderRadius
            width={200}
            loading={loading}
            disabled={!isClinicAdmin}
            onPress={handleSendEmailInviteStaff}
          />
        </View>
      </View>
    </View>
  );

  const renderPatientOptions = () => (
    <View style={{ gap: 10 }}>
      <NormalText text="Search for Patient using their name, phone, or email." />
      {selectedPatient !== null ? (
        <View style={styles.selectedPatientContainer}>
          <input
            type="checkbox"
            checked={true}
            onClick={() => setSelectedPatient(null)}
            style={{
              accentColor: "#1677FF",
            }}
          />
          <View style={styles.patientInfo}>
            <NormalText
              text={`${selectedPatient.firstName} ${selectedPatient.lastName}`}
            />
            <NormalText text={selectedPatient.email} style={styles.smallText} />
            <NormalText text={selectedPatient.phone} style={styles.smallText} />
          </View>
        </View>
      ) : (
        renderPatientSearch()
      )}

      {selectedPatient !== null && (
        <React.Fragment>
          <NormalText
            text={"Send the patient an invite"}
            fontWeight="600"
            style={{ fontSize: 20, marginTop: 10 }}
          />
          <InviteOption
            title="Option 1: Share an Invite Code"
            description="The patient can use this code to sign up with any email or phone number."
          >
            <View style={styles.codeContainer}>
              <NormalText text={patientInviteCode} />
              <TouchableOpacity
                onPress={() => copyToClipboard(patientInviteCode)}
              >
                {copied ? <Check /> : <Copy />}
              </TouchableOpacity>
            </View>
            {errorPatientInviteCode && (
              <NormalText
                text={errorPatientInviteCode}
                style={{ color: "red", textAlign: "center" }}
              />
            )}
          </InviteOption>
        </React.Fragment>
      )}
    </View>
  );

  return (
    <ScrollView
      style={styles.modalView}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.titleContainer}>
        <Text style={styles.title}>Send an invite</Text>
        <Pressable onPress={onClose}>
          <Close />
        </Pressable>
      </View>
      <NormalText
        text="What type of invite do you want to send?"
        style={{ marginVertical: 10 }}
      />
      <View style={styles.buttonRow}>
        {(["Patient", "Staff"] as const).map((type) => (
          <SecondaryButton
            key={type}
            text={type === "Staff" ? "Invite Staff" : `Invite a ${type}`}
            onPress={() => setInviteType(type)}
            selected={inviteType === type}
            roundBorderRadius
          />
        ))}
      </View>
      {!isClinicAdmin && inviteType === "Staff" && (
        <NormalText
          text="Only clinic admins can send invites."
          style={styles.permissionHint}
        />
      )}

      {inviteType === "Staff" && renderStaffOptions()}
      {inviteType === "Patient" && renderPatientOptions()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  modalView: {
    width: "100%",
    height: "100%",
    maxWidth: 465,
    maxHeight: 600,
    padding: 20,
    borderRadius: 10,
    backgroundColor: "#fff",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontFamily: "Urbanist600",
    fontSize: 20,
    lineHeight: 28,
    letterSpacing: 0.2,
  },
  buttonRow: {
    marginHorizontal: "auto",
    flexDirection: "row",
    gap: 10,
    marginBottom: 26,
  },
  staffContainer: {
    gap: 10,
  },
  staffTitle: {
    fontSize: 20,
  },
  optionContainer: {
    borderRadius: 10,
    padding: 10,
    gap: 10,
  },
  optionDescription: {
    marginLeft: 5,
  },
  codeContainer: {
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  emailContainer: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  centeredButton: {
    alignSelf: "center",
  },
  input: {
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    fontFamily: "Urbanist500",
    fontSize: 16,
  },
  scrollView: {
    position: "absolute",
    top: 35,
    width: "100%",
    maxHeight: 200,
    marginTop: 8,
    borderRadius: 10,
  },
  patientListItem: {
    paddingHorizontal: 10,
    flexDirection: "row",
    borderColor: "#DDDEE0",
    borderBottomWidth: 1,
  },
  patientInfo: {
    gap: 4,
    padding: 5,
  },
  smallText: {
    fontSize: 14,
  },
  errorText: {
    color: "red",
  },
  disabledInviteAction: {
    opacity: 0.5,
  },
  permissionHint: {
    color: "red",
    textAlign: "center",
    marginBottom: 16,
  },
  selectedPatientContainer: {
    flexDirection: "row",
    borderRadius: 10,
    borderColor: "#DDDEE0",
    borderWidth: 1,
    paddingHorizontal: 10,
  },
  shadowBox: {
    shadowColor: "rgba(0, 0, 0, 0.08)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 4,
  },
});

export default InviteAMemberModal;
